import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Container, Box,
  IconButton, Drawer, List, ListItem, ListItemText,
  useTheme, useMediaQuery, Chip, Menu, MenuItem,
  Avatar, Divider, Stack, Badge, Grid
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { Link, useNavigate } from 'react-router-dom';

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'Services', path: '/services' },
    { text: 'About', path: '/about' },
    { text: 'Contact', path: '/contact' },
  ];

  const portalLinks = [
    { text: 'Patient Portal', path: '/portal/patient', icon: <AccountCircleIcon /> },
    { text: 'Employee Portal', path: '/portal/employee', icon: <Badge /> },
    { text: 'Admin Dashboard', path: '/admin', icon: <DashboardIcon /> },
  ];

  const drawer = (
    <Box sx={{ width: 300, p: 3, bgcolor: '#0a0a0a', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" color="white" fontWeight={800}>
          🏥 HBMG
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.text} 
            onClick={() => {
              navigate(item.path);
              handleDrawerToggle();
            }}
            sx={{ 
              borderRadius: 2,
              mb: 0.5,
              '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)' }
            }}
          >
            <ListItemText primary={item.text} sx={{ color: 'white' }} />
          </ListItem>
        ))}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
        <Typography variant="overline" color="text.secondary" sx={{ px: 2 }}>
          Portals
        </Typography>
        {portalLinks.map((item) => (
          <ListItem 
            button 
            key={item.text} 
            onClick={() => {
              navigate(item.path);
              handleDrawerToggle();
            }}
            sx={{ 
              borderRadius: 2,
              mb: 0.5,
              '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)' }
            }}
          >
            <ListItemText primary={item.text} sx={{ color: 'white' }} />
          </ListItem>
        ))}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
        <ListItem>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={() => {
              navigate('/book-appointment');
              handleDrawerToggle();
            }}
            sx={{ 
              borderRadius: 50,
              py: 1.5,
              background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
            }}
          >
            Book Appointment
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box>
      {/* Top Bar */}
      <Box sx={{ 
        bgcolor: 'rgba(10, 10, 10, 0.9)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        py: 0.5, 
        display: { xs: 'none', md: 'block' } 
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={3}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b0b0b0' }}>
                <PhoneIcon fontSize="small" sx={{ color: '#00d4ff' }} /> +231-555-0100
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b0b0b0' }}>
                <EmailIcon fontSize="small" sx={{ color: '#00d4ff' }} /> info@hbmg-hospital.digital
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b0b0b0' }}>
                <LocationOnIcon fontSize="small" sx={{ color: '#00d4ff' }} /> Monrovia, Liberia
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button 
                color="inherit" 
                size="small"
                onClick={() => navigate('/portal/patient')}
                startIcon={<AccountCircleIcon />}
                sx={{ 
                  color: '#b0b0b0',
                  '&:hover': { color: '#00d4ff' }
                }}
              >
                Patient Portal
              </Button>
              <Button 
                color="inherit" 
                size="small"
                onClick={() => navigate('/admin')}
                startIcon={<DashboardIcon />}
                sx={{ 
                  color: '#b0b0b0',
                  '&:hover': { color: '#00d4ff' }
                }}
              >
                Admin
              </Button>
              <Chip 
                label="Digital Liberia" 
                size="small" 
                sx={{ 
                  bgcolor: 'rgba(0, 212, 255, 0.15)',
                  color: '#00d4ff',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                }} 
              />
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Navigation */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: 'rgba(10, 10, 10, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'white',
                fontWeight: 800,
                fontSize: '1.3rem',
              }}
            >
              🏥 HBMG Hospital
            </Typography>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    sx={{ 
                      color: '#b0b0b0',
                      fontWeight: 500,
                      '&:hover': { color: '#00d4ff' }
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/book-appointment"
                  sx={{ 
                    borderRadius: 50,
                    px: 3,
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 40px rgba(0, 212, 255, 0.3)',
                    }
                  }}
                >
                  Book Now
                </Button>
                <Button 
                  variant="outlined"
                  onClick={handleMenuOpen}
                  startIcon={<AccountCircleIcon />}
                  sx={{ 
                    borderRadius: 50,
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      borderColor: '#00d4ff',
                      bgcolor: 'rgba(0, 212, 255, 0.1)',
                    }
                  }}
                >
                  Portal
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      bgcolor: '#141414',
                      border: '1px solid rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(20px)',
                      mt: 1,
                    }
                  }}
                >
                  {portalLinks.map((item) => (
                    <MenuItem 
                      key={item.text}
                      onClick={() => { 
                        navigate(item.path); 
                        handleMenuClose(); 
                      }}
                      sx={{ 
                        gap: 2,
                        '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)' }
                      }}
                    >
                      {item.icon}
                      {item.text}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            {isMobile && (
              <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box component="main">
        {children}
      </Box>

      {/* Modern Footer */}
      <Box sx={{ 
        bgcolor: 'rgba(10, 10, 10, 0.95)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        py: 6,
        mt: 4,
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 800, color: 'white' }}>
                🏥 HBMG Hospital
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 2 }}>
                Beyond Treatment. Advancing Liver Health. Transforming Lives.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip label="Digital Liberia" sx={{ borderColor: '#00d4ff' }} />
                <Chip label="Accredited" sx={{ borderColor: '#00ff88' }} />
              </Stack>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'white' }}>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                <Link to="/portal/patient" style={{ color: '#b0b0b0', textDecoration: 'none' }}>Patient Portal</Link>
                <Link to="/portal/employee" style={{ color: '#b0b0b0', textDecoration: 'none' }}>Employee Portal</Link>
                <Link to="/book-appointment" style={{ color: '#b0b0b0', textDecoration: 'none' }}>Book Appointment</Link>
              </Stack>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'white' }}>
                Services
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ color: '#b0b0b0' }}>Hepatology</Typography>
                <Typography variant="body2" sx={{ color: '#b0b0b0' }}>Infectious Disease</Typography>
                <Typography variant="body2" sx={{ color: '#b0b0b0' }}>Internal Medicine</Typography>
                <Typography variant="body2" sx={{ color: '#b0b0b0' }}>Diagnostic Imaging</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'white' }}>
                Contact
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b0b0b0' }}>
                  <LocationOnIcon fontSize="small" sx={{ color: '#00d4ff' }} /> Monrovia, Liberia
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b0b0b0' }}>
                  <PhoneIcon fontSize="small" sx={{ color: '#00d4ff' }} /> +231-555-0100
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b0b0b0' }}>
                  <EmailIcon fontSize="small" sx={{ color: '#00d4ff' }} /> info@hbmg-hospital.digital
                </Typography>
                <Chip 
                  label="🚨 Emergency: 24/7" 
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(255, 107, 107, 0.15)',
                    color: '#ff6b6b',
                    border: '1px solid rgba(255, 107, 107, 0.2)',
                    mt: 1,
                  }} 
                />
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 3 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              © 2026 HBMG Hospital. All rights reserved. | Part of the Digital Liberia Ecosystem
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
